import classNames from "classnames";
import { useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/authContext";
import useOnClickOutside from "../hooks/useOnClickOutside";
import useToggle from "../hooks/useToggle";
import { useTranslations } from "../hooks/useTranslations";
import { useInvites } from "../http/serviceportalApi";
import SVGLogout from "../icons/Logout.svg?react";
import SVGOpenInNew from "../icons/OpenInNew.svg?react";
import SVGOutbox from "../icons/Outbox.svg?react";
import SVGPerson from "../icons/Person.svg?react";
import SVGTranslate from "../icons/Translate.svg?react";
import { isRouteEconomicAdvisors } from "../utils";
import styles from "./AccountButton.module.css";
import Icon from "./ui/Icon";
import Profile from "./ui/Profile";
import Select from "./ui/Select";

const AccountButton = () => {
  const { currentLanguage, i18n, supportedLanguages, setLanguage } =
    useTranslations();

  const ref = useRef<HTMLDivElement>(null);
  const { user, logout } = useAuth();
  const menu = useToggle();
  const { data: invitesData } = useInvites({ page: 1, page_size: 1 });
  const hasPendingInvites = (invitesData?.items.length ?? 0) > 0;
  const location = useLocation();

  useOnClickOutside(ref, menu.close);

  if (!user) {
    return null;
  }

  return (
    <div ref={ref} className={styles.wrapper}>
      <button className={styles.button} onClick={menu.toggle}>
        <Profile
          user={user}
          layout="compact"
          hasNotifications={hasPendingInvites}
        />
      </button>
      {menu.isOpen && (
        <div className={styles.menu}>
          <Profile user={user} className={styles.profile} layout="full" />
          <hr className={styles.separator} />
          <ul className={styles.list}>
            <li>
              <Link
                to="https://accounts.hgv.it"
                target="_blank"
                className={styles.menuItem}
                onClick={menu.close}
              >
                <Icon glyph={SVGPerson} className={styles.icon} />
                {i18n.accountButton.account}
                <Icon glyph={SVGOpenInNew} className={styles.openInNew} />
              </Link>
            </li>
            <li>
              <div className={styles.languageSelector}>
                <div className={styles.languageLabel}>
                  <Icon glyph={SVGTranslate} className={styles.icon} />
                  {i18n.accountButton.language}
                </div>
                <Select
                  value={currentLanguage}
                  className={styles.languageSelect}
                  onChange={(e) => {
                    setLanguage(e.target.value);
                  }}
                >
                  {supportedLanguages.map((language) => (
                    <option key={language} value={language}>
                      {language}
                    </option>
                  ))}
                </Select>
              </div>
            </li>
            {!isRouteEconomicAdvisors(location) && (
              <li>
                <Link
                  to="/einladungen"
                  className={styles.menuItem}
                  onClick={menu.close}
                >
                  <Icon glyph={SVGOutbox} className={styles.icon} />
                  <span
                    className={classNames({
                      [styles.unread]: hasPendingInvites,
                    })}
                  >
                    {i18n.accountButton.invites}
                  </span>
                </Link>
              </li>
            )}
            <li>
              <button className={styles.menuItem} onClick={logout}>
                <Icon glyph={SVGLogout} className={styles.icon} />
                {i18n.accountButton.logout}
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default AccountButton;
